import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIdentity } from '../hooks/useIdentity';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { ErrorDisplay } from './ErrorDisplay';
import { getStoredRelays } from '../utils/relay';
import { GraphResolver } from '../services/graphResolver';
import { SyncService } from '../services/syncService';
import { useRelayNotifications } from '../hooks/useRelayNotifications';
import type { ServiceStatus } from '../types/identity';

interface ServiceSyncConfig {
  service_uuid: string;
  enabled: boolean;
  frequency: 'min' | 'hour' | 'day';
  windowHours: number;
  useBloom: boolean;
  useMerkle: boolean;
  lastSync?: number;
}

export function ServiceSyncScreen(): JSX.Element {
  const navigate = useNavigate();
  const { identity } = useIdentity();
  const { error, handleError, clearError } = useErrorHandler();
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [configs, setConfigs] = useState<Map<string, ServiceSyncConfig>>(
    new Map(),
  );
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState<
    Map<string, { ok: boolean; message?: string }>
  >(new Map());

  const graphResolverRef = useRef<GraphResolver | null>(null);
  if (graphResolverRef.current === null) {
    graphResolverRef.current = new GraphResolver();
  }
  const { startPolling, stopPolling } = useRelayNotifications(
    graphResolverRef.current,
    true,
  );

  // Load services and configs on mount
  useEffect(() => {
    if (identity === null || graphResolverRef.current === null) {
      return;
    }

    const graphResolver = graphResolverRef.current;
    const syncService = new SyncService(
      getStoredRelays().filter((r) => r.enabled),
      graphResolver,
      identity,
    );

    void (async (): Promise<void> => {
      await syncService.init();
      const svcs = graphResolver.getServices();
      setServices(
        svcs.map((s) => ({
          service_uuid: s.uuid,
          contrat_complet: true,
          contrat_valide: true,
        })),
      );

      // Load configs from localStorage
      const stored = localStorage.getItem('userwallet_service_sync_configs');
      if (stored !== null) {
        try {
          const parsed = JSON.parse(stored) as Record<
            string,
            ServiceSyncConfig
          >;
          setConfigs(new Map(Object.entries(parsed)));
        } catch {
          // Ignore invalid stored configs
        }
      }
    })();
  }, [identity]);

  // Start/stop polling based on configs
  useEffect(() => {
    const enabledConfigs = Array.from(configs.values()).filter(
      (c) => c.enabled,
    );
    if (enabledConfigs.length > 0) {
      const minInterval = Math.min(
        ...enabledConfigs.map((c) => {
          switch (c.frequency) {
            case 'min':
              return 60000;
            case 'hour':
              return 3600000;
            case 'day':
              return 86400000;
            default:
              return 3600000;
          }
        }),
      );
      startPolling(minInterval);
      return () => {
        stopPolling();
      };
    }
    return undefined;
  }, [configs, startPolling, stopPolling]);

  const saveConfigs = useCallback((newConfigs: Map<string, ServiceSyncConfig>): void => {
    const obj = Object.fromEntries(newConfigs);
    localStorage.setItem('userwallet_service_sync_configs', JSON.stringify(obj));
    setConfigs(newConfigs);
  }, []);

  const getConfig = useCallback(
    (serviceUuid: string): ServiceSyncConfig => {
      const existing = configs.get(serviceUuid);
      if (existing !== undefined) {
        return existing;
      }
      return {
        service_uuid: serviceUuid,
        enabled: false,
        frequency: 'hour',
        windowHours: 24,
        useBloom: true,
        useMerkle: false,
      };
    },
    [configs],
  );

  const updateConfig = useCallback(
    (serviceUuid: string, updates: Partial<ServiceSyncConfig>): void => {
      const current = getConfig(serviceUuid);
      const updated = { ...current, ...updates };
      const newConfigs = new Map(configs);
      newConfigs.set(serviceUuid, updated);
      saveConfigs(newConfigs);
    },
    [configs, getConfig, saveConfigs],
  );

  const handleSyncNow = useCallback(
    async (serviceUuid: string): Promise<void> => {
      if (identity === null) {
        handleError('Identité requise', 'NO_IDENTITY');
        return;
      }

      setIsSyncing(true);
      clearError();

      try {
        const config = getConfig(serviceUuid);
        const now = Date.now();
        const start = now - config.windowHours * 3600000;

        if (graphResolverRef.current === null) {
          graphResolverRef.current = new GraphResolver();
        }
        const graphResolver = graphResolverRef.current;
        const syncService = new SyncService(
          getStoredRelays().filter((r) => r.enabled),
          graphResolver,
          identity,
        );
        await syncService.init();

        // Use Bloom filter if enabled
        if (config.useBloom) {
          // Bloom filter is already used in syncService via getKeysInWindow
          // which skips hashes already seen
        }

        const result = await syncService.sync(start, now, serviceUuid);

        const newResults = new Map(syncResults);
        const hasMessages = result.messages > 0 || result.newMessages > 0;
        newResults.set(serviceUuid, {
          ok: hasMessages,
          message: hasMessages
            ? `Sync OK: ${result.newMessages} nouveaux messages`
            : 'Aucun nouveau message',
        });
        setSyncResults(newResults);

        // Update last sync
        updateConfig(serviceUuid, { lastSync: now });
      } catch (err) {
        handleError(err, 'Erreur lors de la synchronisation');
        const newResults = new Map(syncResults);
        newResults.set(serviceUuid, {
          ok: false,
          message: err instanceof Error ? err.message : 'Erreur inconnue',
        });
        setSyncResults(newResults);
      } finally {
        setIsSyncing(false);
      }
    },
    [identity, getConfig, syncResults, updateConfig, handleError, clearError],
  );


  return (
    <main>
      <h1>Synchronisation continue par service</h1>
      {error !== null && <ErrorDisplay error={error} onDismiss={clearError} />}

      <section aria-labelledby="services-list">
        <h2 id="services-list">Services</h2>
        {services.length === 0 ? (
          <p>Aucun service disponible. Effectuez une synchronisation globale d'abord.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Service UUID</th>
                <th>Sync auto</th>
                <th>Fréquence</th>
                <th>Fenêtre (h)</th>
                <th>Bloom</th>
                <th>Merkle</th>
                <th>Dernière sync</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {services.map((svc) => {
                const config = getConfig(svc.service_uuid);
                const result = syncResults.get(svc.service_uuid);
                return (
                  <tr key={svc.service_uuid}>
                    <td>{svc.service_uuid.slice(0, 16)}...</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={config.enabled}
                        onChange={(e): void => {
                          updateConfig(svc.service_uuid, {
                            enabled: e.target.checked,
                          });
                        }}
                      />
                    </td>
                    <td>
                      <select
                        value={config.frequency}
                        onChange={(e): void => {
                          updateConfig(svc.service_uuid, {
                            frequency: e.target.value as 'min' | 'hour' | 'day',
                          });
                        }}
                      >
                        <option value="min">Minute</option>
                        <option value="hour">Heure</option>
                        <option value="day">Jour</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        min="1"
                        max="168"
                        value={config.windowHours}
                        onChange={(e): void => {
                          updateConfig(svc.service_uuid, {
                            windowHours: parseInt(e.target.value, 10) || 24,
                          });
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={config.useBloom}
                        onChange={(e): void => {
                          updateConfig(svc.service_uuid, {
                            useBloom: e.target.checked,
                          });
                        }}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={config.useMerkle}
                        onChange={(e): void => {
                          updateConfig(svc.service_uuid, {
                            useMerkle: e.target.checked,
                          });
                        }}
                      />
                      {config.useMerkle && (
                        <span style={{ color: 'orange', fontSize: '0.8em' }}>
                          {' '}
                          (Non implémenté)
                        </span>
                      )}
                    </td>
                    <td>
                      {config.lastSync !== undefined
                        ? new Date(config.lastSync).toLocaleString()
                        : '—'}
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={(): void => {
                          void handleSyncNow(svc.service_uuid);
                        }}
                        disabled={isSyncing}
                      >
                        {isSyncing ? 'Sync...' : 'Lancer maintenant'}
                      </button>
                      {result !== undefined && (
                        <div
                          style={{
                            fontSize: '0.8em',
                            color: result.ok ? 'green' : 'red',
                          }}
                        >
                          {result.message}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section aria-labelledby="info">
        <h2 id="info">Informations</h2>
        <ul>
          <li>
            <strong>Bloom filter</strong> : Réduit les requêtes inutiles en
            évitant de fetch des clés pour des hash déjà connus.
          </li>
          <li>
            <strong>Merkle trees</strong> : Accélération de scan pour volumes
            importants (non implémenté).
          </li>
          <li>
            <strong>Fenêtre</strong> : Période de scan en heures (1-168).
          </li>
          <li>
            <strong>Fréquence</strong> : Intervalle entre synchronisations
            automatiques.
          </li>
        </ul>
      </section>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        <button
          type="button"
          onClick={(): void => {
            navigate('/sync');
          }}
        >
          Sync globale
        </button>
        <button
          type="button"
          onClick={(): void => {
            navigate('/');
          }}
        >
          Retour
        </button>
      </div>
    </main>
  );
}
